import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CustomerHeader from '../../components/ui/CustomerHeader';
import CartItem from './components/CartItem';
import OrderSummary from './components/OrderSummary';
import EmptyCart from './components/EmptyCart';
import Footer from '../../components/ui/Footer';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const wishlist = useWishlist();

  // Defensive normalization — guard against any partial older items.
  const cartItems = (cart.items || []).map((it) => ({
    ...it,
    id: it?.id ?? '',
    name: it?.name ?? '',
    brand: it?.brand ?? '',
    price: typeof it?.price === 'number' ? it.price : 0,
    quantity: typeof it?.quantity === 'number' && it.quantity > 0 ? it.quantity : 1,
    image: it?.image ?? '',
    imageAlt: it?.imageAlt ?? it?.name ?? '',
  }));

  // Self-heal: backfill missing item images from the products feed.
  const API_BASE    = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_STOREFRONT_API_URL) || 'https://dashboard.offloadhost.com';
  const CUSTOMER_ID = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_CUSTOMER_ID) || '';
  const [imageById, setImageById] = useState({});
  useEffect(() => {
    if (!CUSTOMER_ID) return;
    if (!cartItems.some((it) => !it.image)) return;
    const ctl = new AbortController();
    fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/products`, { signal: ctl.signal })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const map = {};
        (data?.products || []).forEach((p) => { map[String(p.id)] = p.image_url || ''; });
        setImageById(map);
      })
      .catch(() => {});
    return () => ctl.abort();
  }, [cartItems.length]);

  const subtotal = cartItems.reduce((sum, it) => sum + (it.price * it.quantity), 0);
  const shipping = subtotal >= 50 ? 0 : (subtotal > 0 ? 9.99 : 0);
  const tax = subtotal * 0.08;
  const discount = 0;
  const total = subtotal + shipping + tax - discount;

  const handleUpdateQuantity = (id, qty) => cart.updateQuantity(id, qty);
  const handleRemoveItem    = (id)      => cart.removeItem(id);
  const handleMoveToWishlist = (id) => {
    const it = cartItems.find((x) => x.id === id);
    if (it) { wishlist.toggle(it); cart.removeItem(id); }
  };
  const handleApplyPromoCode = () => {};
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const handleProceedToCheckout = () => {
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div className="bg-background min-h-screen flex flex-col">
        <CustomerHeader />
        <EmptyCart recentlyViewed={[]} popularProducts={[]} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader />

      <div className="pt-16 pb-16 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">Shopping Cart</h1>
                <p className="text-muted-foreground mt-2">
                  {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/product-catalog')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Continue Shopping
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {cartItems.map((item) => (
                <CartItem
                  imageById={imageById}
                  key={item.id}
                  item={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={handleRemoveItem}
                  onMoveToWishlist={handleMoveToWishlist}
                />
              ))}
            </div>
            <div className="lg:col-span-1">
              <OrderSummary
                subtotal={subtotal}
                shipping={shipping}
                tax={tax}
                discount={discount}
                total={total}
                onApplyPromoCode={handleApplyPromoCode}
                onProceedToCheckout={handleProceedToCheckout}
                isCheckoutLoading={isCheckoutLoading}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ShoppingCart;
